/**
 * NGXS State for Award Committee Meetings
 */

import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Page, PageMetadata } from '@tran-monorepo/interfaces';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { AwardCommitteeMeeting } from '../interfaces/award-committee-meeting.interface';
import { AwardCommitteeStateModel } from '../interfaces/award-committee-state.interface';
import { AwardCommitteeService } from '../services/award-committee.service';
import {
    CreateDeadline,
    CreateMeeting,
    DeleteDeadline,
    DeleteMeeting,
    LoadMeetings,
    UpdateDeadline,
    UpdateMeeting
} from './award-committee.actions';

const defaults: AwardCommitteeStateModel = {
    meetings: [],
    pagination: null,
    isLoading: false
};

@State<AwardCommitteeStateModel>({
    name: 'awardCommittee',
    defaults
})
@Injectable()
export class AwardCommitteeState {
    private readonly awardCommitteeService = inject(AwardCommitteeService);

    // ==================== Selectors ====================

    @Selector()
    static meetings(state: AwardCommitteeStateModel): AwardCommitteeMeeting[] {
        return state.meetings;
    }

    @Selector()
    static pagination(state: AwardCommitteeStateModel): PageMetadata | null {
        return state.pagination;
    }

    @Selector()
    static isLoading(state: AwardCommitteeStateModel): boolean {
        return state.isLoading;
    }

    // ==================== Meeting Actions ====================

    @Action(LoadMeetings)
    public loadMeetings(
        ctx: StateContext<AwardCommitteeStateModel>,
        action: LoadMeetings
    ): Observable<Page<AwardCommitteeMeeting, PageMetadata>> {
        ctx.patchState({ isLoading: true });

        return this.awardCommitteeService.getMeetings(action.page, action.pageSize).pipe(
            tap(response => {
                ctx.patchState({
                    meetings: response.items,
                    pagination: response.metadata
                });
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }

    @Action(CreateMeeting)
    public createMeeting(
        ctx: StateContext<AwardCommitteeStateModel>,
        action: CreateMeeting
    ): Observable<AwardCommitteeMeeting> {
        ctx.patchState({ isLoading: true });

        return this.awardCommitteeService.createMeeting(action.request).pipe(
            tap(newMeeting => {
                const state = ctx.getState();
                ctx.patchState({
                    meetings: [...state.meetings, newMeeting]
                });
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }

    @Action(UpdateMeeting)
    public updateMeeting(
        ctx: StateContext<AwardCommitteeStateModel>,
        action: UpdateMeeting
    ): Observable<AwardCommitteeMeeting> {
        ctx.patchState({ isLoading: true });

        return this.awardCommitteeService.updateMeeting(action.id, action.request).pipe(
            tap(updatedMeeting => {
                const state = ctx.getState();
                const meetings = state.meetings.map(meeting =>
                    meeting.id === action.id ? { ...meeting, ...updatedMeeting } : meeting
                );
                ctx.patchState({ meetings });
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }

    @Action(DeleteMeeting)
    public deleteMeeting(ctx: StateContext<AwardCommitteeStateModel>, action: DeleteMeeting): Observable<void> {
        ctx.patchState({ isLoading: true });

        return this.awardCommitteeService.deleteMeeting(action.id).pipe(
            tap(() => {
                const state = ctx.getState();
                ctx.patchState({
                    meetings: state.meetings.filter(meeting => meeting.id !== action.id)
                });
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }

    // ==================== Deadline Actions ====================

    @Action(CreateDeadline)
    public createDeadline(ctx: StateContext<AwardCommitteeStateModel>, action: CreateDeadline): Observable<unknown> {
        ctx.patchState({ isLoading: true });

        return this.awardCommitteeService.createDeadline(action.meetingId, action.request).pipe(
            tap(newDeadline => {
                const state = ctx.getState();
                const meetings = state.meetings.map(meeting => {
                    if (meeting.id === action.meetingId) {
                        return {
                            ...meeting,
                            deadlines: [...(meeting.deadlines ?? []), newDeadline]
                        };
                    }
                    return meeting;
                });
                ctx.patchState({ meetings });
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }

    @Action(UpdateDeadline)
    public updateDeadline(ctx: StateContext<AwardCommitteeStateModel>, action: UpdateDeadline): Observable<unknown> {
        ctx.patchState({ isLoading: true });

        return this.awardCommitteeService.updateDeadline(action.meetingId, action.deadlineId, action.request).pipe(
            tap(updatedDeadline => {
                const state = ctx.getState();
                const meetings = state.meetings.map(meeting => {
                    if (meeting.id === action.meetingId) {
                        return {
                            ...meeting,
                            deadlines: (meeting.deadlines ?? []).map(deadline =>
                                deadline.id === action.deadlineId ? { ...deadline, ...updatedDeadline } : deadline
                            )
                        };
                    }
                    return meeting;
                });
                ctx.patchState({ meetings });
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }

    @Action(DeleteDeadline)
    public deleteDeadline(ctx: StateContext<AwardCommitteeStateModel>, action: DeleteDeadline): Observable<void> {
        ctx.patchState({ isLoading: true });

        return this.awardCommitteeService.deleteDeadline(action.meetingId, action.deadlineId).pipe(
            tap(() => {
                const state = ctx.getState();
                const meetings = state.meetings.map(meeting => {
                    if (meeting.id === action.meetingId) {
                        return {
                            ...meeting,
                            deadlines: (meeting.deadlines ?? []).filter(deadline => deadline.id !== action.deadlineId)
                        };
                    }
                    return meeting;
                });
                ctx.patchState({ meetings });
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }
}
